import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import TopBar from "@/components/TopBar";
import Player from "@/components/Player";
import CRTStatic from "@/components/CRTStatic";
import LoadingSignal from "@/components/LoadingSignal";
import InfoPanel from "@/components/InfoPanel";
import HistoryPanel from "@/components/HistoryPanel";
import FavoritesPanel from "@/components/FavoritesPanel";
import ArchivePanel from "@/components/ArchivePanel";
import TimeDial from "@/components/TimeDial";
import ModeSelector from "@/components/ModeSelector";
import Controls from "@/components/Controls";
import { useRandomVideo } from "@/hooks/useRandomVideo";
import { useMouseActivity } from "@/hooks/useMouseActivity";
import { useKeyboard } from "@/hooks/useKeyboard";
import { useChannelSound } from "@/hooks/useChannelSound";
import { usePersistentStore } from "@/stores/playerStore";
import type { RandomMode } from "@/types/video";
import { formatDate, formatNumber } from "@/services/api";

export default function Watch() {
  // Mode / filter state
  const [mode, setMode] = useState<RandomMode>("random");
  const [year, setYear] = useState<number | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [unknownMode, setUnknownMode] = useState(false);

  // UI panel state
  const [infoOpen, setInfoOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  const [modeOpen, setModeOpen] = useState(false);
  const [timeDialVisible, setTimeDialVisible] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  // Hydrate last-used filter from localStorage on mount
  useEffect(() => {
    const lastMode = usePersistentStore.getState().lastMode;
    const lastYear = usePersistentStore.getState().lastYear;
    const lastCategory = usePersistentStore.getState().lastCategory;
    const settings = usePersistentStore.getState().settings;
    if (lastMode) setMode(lastMode as RandomMode);
    if (lastYear) setYear(lastYear);
    if (lastCategory) setCategory(lastCategory);
    setUnknownMode(settings.unknownMode);
  }, []);

  // Video fetching
  const {
    current,
    loading,
    error,
    channelSwitching,
    changeChannel,
    goPrevious,
    refreshParams,
  } = useRandomVideo({ mode, year, category });

  // Channel-switch sound (Web Audio synthesized)
  const sound = useChannelSound();
  const blockedTags = usePersistentStore((state) => state.blockedTags);
  const recordVideoViewed = usePersistentStore((state) => state.recordVideoViewed);
  const recordWatchTime = usePersistentStore((state) => state.recordWatchTime);
  const blockTag = usePersistentStore((state) => state.blockTag);

  // Sync filter → random hook + persist
  useEffect(() => {
    refreshParams({ mode, year, category, blockedTags });
    usePersistentStore.getState().setLastFilter(mode, year, category);
  }, [mode, year, category, blockedTags, refreshParams]);

  // Add to history whenever current changes
  useEffect(() => {
    if (!current) return;
    usePersistentStore.getState().addHistory({
      bvid: current.bvid,
      title: current.title,
      year: current.year,
      category: current.category,
      author: current.author.name,
      watchedAt: Date.now(),
    });
  }, [current?.bvid]);

  // Count unique videos and only the time the current page is visible.
  useEffect(() => {
    if (!current) return;
    recordVideoViewed(current);
    let active = document.visibilityState === "visible";
    let startedAt = Date.now();
    const flush = () => {
      if (active) recordWatchTime(Date.now() - startedAt);
      startedAt = Date.now();
    };
    const onVisibilityChange = () => {
      flush();
      active = document.visibilityState === "visible";
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      flush();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [current?.bvid, recordVideoViewed, recordWatchTime]);

  // Persist unknownMode changes
  useEffect(() => {
    usePersistentStore.getState().updateSettings({ unknownMode });
  }, [unknownMode]);

  const uiVisible = useMouseActivity();

  // Favorites state
  const isFav = usePersistentStore((s) =>
    current ? s.favorites.some((f) => f.bvid === current.bvid) : false
  );
  const toggleFavorite = usePersistentStore((s) => s.toggleFavorite);

  const handleToggleFavorite = useCallback(() => {
    if (!current) return;
    toggleFavorite({
      bvid: current.bvid,
      title: current.title,
      year: current.year,
      category: current.category,
      author: current.author.name,
      addedAt: Date.now(),
    });
  }, [current, toggleFavorite]);

  // Wrap channel change with sound effect
  const handleChangeChannel = useCallback((blockedTagsOverride?: string[]) => {
    sound.play();
    changeChannel(blockedTagsOverride);
  }, [sound, changeChannel]);

  const handleGoPrevious = useCallback(() => {
    sound.play();
    goPrevious();
  }, [sound, goPrevious]);

  // Keyboard
  useKeyboard(
    {
      onChangeChannel: () => handleChangeChannel(),
      onPrevious: () => handleGoPrevious(),
      onInfo: () => setInfoOpen((v) => !v),
      onHistory: () => setHistoryOpen((v) => !v),
      onFavorites: () => setFavoritesOpen((v) => !v),
      onMode: () => setModeOpen((v) => !v),
      onTimeDial: () => setTimeDialVisible((v) => !v),
      onArchive: () => setArchiveOpen((v) => !v),
      onEscape: () => {
        setInfoOpen(false);
        setHistoryOpen(false);
        setFavoritesOpen(false);
        setModeOpen(false);
        setArchiveOpen(false);
      },
    },
    true
  );

  const handleSelectMode = (m: RandomMode, cat: string | null) => {
    setMode(m);
    setCategory(cat);
    setModeOpen(false);
    setTimeout(() => handleChangeChannel(), 100);
  };

  const handleOpenArchive = () => {
    setInfoOpen(false);
    setHistoryOpen(false);
    setFavoritesOpen(false);
    setModeOpen(false);
    setArchiveOpen(true);
  };

  return (
    <div className="acg-watch fixed inset-0 bg-void text-phosphor overflow-hidden select-none">
      {/* Player container */}
      <div className="absolute inset-0">
        <Player video={current} />
      </div>

      {/* Fixed CRT scanlines overlay — always on, blends with video */}
      <div className="absolute inset-0 crt-scanlines-static pointer-events-none z-10" />
      <div className="absolute inset-0 crt-scanlines-drift pointer-events-none z-10" />
      <div className="absolute inset-0 crt-vignette pointer-events-none z-10" />

      {/* CRT channel-switch burst */}
      <CRTStatic active={channelSwitching} />

      {/* Initial loading */}
      <AnimatePresence>
        {loading && !current && (
          <motion.div
            key="loader"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <LoadingSignal />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Full-screen error overlay — only when the API itself is unreachable */}
      <AnimatePresence>
        {error && !current && (
          <motion.div
            className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-4 bg-void/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-ui-zone
          >
            <div className="vfd text-base animate-pulse">SIGNAL LOST</div>
            <p className="font-mono text-[10px] text-static tracking-widest2 opacity-70 text-center max-w-xs">
              无法连接到 Bilibili 服务器
            </p>
            <button
              onClick={() => handleChangeChannel()}
              className="font-mono text-xs tracking-widest2 text-static hover:text-signal transition-colors mt-2"
            >
              [ 重试换台 ]
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top bar */}
      <TopBar
        video={current}
        loading={loading}
        switching={channelSwitching}
        unknownMode={unknownMode}
        mode={mode}
        year={year}
        visible={uiVisible && !loading}
        onOpenHistory={() => setHistoryOpen(true)}
        onOpenFavorites={() => setFavoritesOpen(true)}
        onOpenMode={() => setModeOpen(true)}
        onOpenArchive={handleOpenArchive}
      />

      {/* Right-side channel-info badge */}
      {current && !unknownMode && uiVisible && !loading && (
        <motion.div
          className="absolute right-4 sm:right-6 bottom-28 z-20 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: uiVisible ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="text-right font-mono text-[10px] sm:text-xs text-static space-y-1 tracking-widest2">
            <div>{current.year}</div>
            <div>{current.category?.toUpperCase()}</div>
            <div className="text-signal">SIGNAL OK</div>
          </div>
        </motion.div>
      )}

      {/* Bottom controls */}
      <div className="absolute inset-x-0 bottom-6 sm:bottom-10 z-30 px-4 safe-bottom">
        <Controls
          isFavorite={isFav}
          onToggleFavorite={handleToggleFavorite}
          onChangeChannel={() => handleChangeChannel()}
          onToggleInfo={() => setInfoOpen((v) => !v)}
          visible={uiVisible && !loading}
        />

        {/* Time dial */}
        <div className="mt-6 max-w-2xl mx-auto">
          <TimeDial
            value={year}
            onChange={(y) => {
              setYear(y);
              setMode("archive");
            }}
            onClose={() => setTimeDialVisible(false)}
            visible={timeDialVisible && uiVisible}
          />
        </div>

        {/* Hint strip */}
        <div className="mt-4 text-center font-mono text-[9px] sm:text-[10px] text-dim tracking-widest2 pointer-events-none">
          SPACE · 换台　← → 频道　I 信息　H 历史　L 收藏　R 频道　T 时间
        </div>
      </div>

      {/* Slide-in panels */}
      <InfoPanel
        video={current}
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        onChangeChannel={() => {
          setInfoOpen(false);
          handleChangeChannel();
        }}
        onBlockTag={(tag) => {
          blockTag(tag);
          setInfoOpen(false);
        }}
      />
      <HistoryPanel open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <FavoritesPanel
        open={favoritesOpen}
        onClose={() => setFavoritesOpen(false)}
      />
      <ModeSelector
        open={modeOpen}
        onClose={() => setModeOpen(false)}
        currentMode={mode}
        currentCategory={category}
        onSelect={handleSelectMode}
      />
      <ArchivePanel
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        onBlockTag={blockTag}
      />

      {/* Mobile-only quick actions */}
      <div className="sm:hidden fixed inset-x-0 top-0 z-40 safe-top pointer-events-none">
        <div
          className="flex justify-end p-3 gap-3 pointer-events-auto"
          data-ui-zone
        >
          <button
            onClick={() => setModeOpen(true)}
            className="font-mono text-[10px] tracking-widest2 text-static hover:text-signal"
          >
            CH
          </button>
          <button
            onClick={() => setHistoryOpen(true)}
            className="font-mono text-[10px] tracking-widest2 text-static hover:text-signal"
          >
            HIS
          </button>
          <button
            onClick={() => setFavoritesOpen(true)}
            className="font-mono text-[10px] tracking-widest2 text-static hover:text-signal"
          >
            TAP
          </button>
          <button
            onClick={() => setTimeDialVisible((v) => !v)}
            className="font-mono text-[10px] tracking-widest2 text-static hover:text-signal"
          >
            T
          </button>
          <button
            onClick={handleOpenArchive}
            className="font-mono text-[10px] tracking-widest2 text-static hover:text-signal"
          >
            ARC
          </button>
        </div>
      </div>

      {/* Unknown mode reveal toast */}
      <UnknownReveal video={current} active={unknownMode && infoOpen} />
    </div>
  );
}

function UnknownReveal({
  video,
  active,
}: {
  video: import("@/types/video").Video | null;
  active: boolean;
}) {
  return (
    <AnimatePresence>
      {active && video && (
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40 glass-panel px-8 py-6 max-w-md text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3 }}
          data-ui-zone
        >
          <div className="vfd text-xs tracking-widest2 mb-4">
            SIGNAL IDENTIFIED
          </div>
          <div className="font-mono text-base text-phosphor mb-2">
            {video.title}
          </div>
          <div className="font-mono text-xs text-static space-y-1 tracking-wide">
            <div>{formatDate(video.pubdate)}</div>
            <div>UP: {video.author.name ?? "未知"}</div>
            <div>{video.bvid}</div>
            <div className="text-dim mt-2">
              {formatNumber(video.view)} 次播放
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
